import { useState, useRef, type FC, type ReactElement, type RefObject } from 'react';
import { graphql, useFragment } from 'react-relay';

import { ProductBaseTile } from './ProductBaseTile';
import { ProductImage } from './Carousel/ProductImage';

import { type MeasurementUnit } from 'dibs-intl/exports/units';
import {
    getBackgroundColor,
    type ImageBackgroundColor,
    type OnClick,
    type SrcSet,
    type TileSize,
} from './constants';

import { type BuyerProductTile_viewer$key } from './__generated__/BuyerProductTile_viewer.graphql';
import { type BuyerProductTile_item$key } from './__generated__/BuyerProductTile_item.graphql';
import { type BuyerProductTile_user$key } from './__generated__/BuyerProductTile_user.graphql';
import { type BuyerProductTile_itemSearch$key } from './__generated__/BuyerProductTile_itemSearch.graphql';

const viewerFragment = graphql`
    fragment BuyerProductTile_viewer on Viewer {
        ...ProductBaseTile_viewer
    }
`;

const itemSearchFragment = graphql`
    fragment BuyerProductTile_itemSearch on ItemSearchQueryConnection {
        ...ProductBaseTile_itemSearch
    }
`;

const itemFragment = graphql`
    fragment BuyerProductTile_item on Item
    @argumentDefinitions(
        isTrade: { type: "Boolean", defaultValue: false }
        pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
        photosLimit: { type: "Int", defaultValue: 1 }
        priceBookName: { type: "String" }
        showSeller: { type: "Boolean!" }
    ) {
        productPhotos: photos(limit: $photosLimit) {
            ...ProductImage_photo
        }
        ...ProductBaseTile_item
            @arguments(
                isTrade: $isTrade
                pageDisplayEnum: $pageDisplayEnum
                priceBookName: $priceBookName
                showSeller: $showSeller
            )
        ...ProductImage_item
    }
`;

const userFragment = graphql`
    fragment BuyerProductTile_user on User @argumentDefinitions(showSeller: { type: "Boolean!" }) {
        ...ProductBaseTile_user @arguments(showSeller: $showSeller)
    }
`;

type Props = {
    viewer: BuyerProductTile_viewer$key | null | undefined;
    itemSearch: BuyerProductTile_itemSearch$key | null | undefined;
    item: BuyerProductTile_item$key | null | undefined;
    user: BuyerProductTile_user$key | null | undefined;
    afterDisplay: boolean;
    beforeRender?: () => void;
    currency: string;
    disableLinks?: boolean;
    imageBackgroundColor?: ImageBackgroundColor;
    imageLoadVerticalOffset?: number;
    index: number;
    isSponsored?: boolean;
    itemsPerRow: number;
    hideRecentSoldPrice?: boolean;
    measurementUnit?: MeasurementUnit;
    shippingPrices?: Record<string, number> | null;
    onClick?: OnClick;
    onContentLoaded?: () => void;
    onContentVisible?: () => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    showMskuSwatch?: boolean;
    showBrowseSimilar?: boolean;
    showMeasurements?: boolean;
    showPrice?: boolean;
    showShippingCost?: boolean;
    hasQuickView?: boolean;
    showQuickViewButton?: boolean;
    onQuickViewDetailsClick?: () => void;
    size: TileSize;
    favoritesComponent?: ReactElement | null;
    srcSet?: SrcSet;
    urgencySignal?: string | null;
    isShippingCostSbVariant?: boolean;
    tileElementRef?: RefObject<HTMLDivElement>;
};

export const BuyerProductTile: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    item: itemRef,
    user: userRef,
    afterDisplay,
    beforeRender,
    currency,
    disableLinks,
    imageBackgroundColor,
    imageLoadVerticalOffset,
    index,
    isSponsored,
    itemsPerRow,
    hideRecentSoldPrice,
    measurementUnit,
    shippingPrices,
    onClick,
    onContentLoaded,
    onContentVisible,
    onMouseEnter,
    onMouseLeave,
    size,
    showMskuSwatch = true,
    showBrowseSimilar,
    showMeasurements,
    showPrice,
    showShippingCost,
    hasQuickView = true,
    showQuickViewButton = true,
    srcSet,
    favoritesComponent,
    urgencySignal,
    isShippingCostSbVariant,
    tileElementRef,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);

    const [isHovering, setIsHovering] = useState(false);
    const wasHovered = useRef(false);
    const backgroundColor = getBackgroundColor(imageBackgroundColor);
    const photos = item?.productPhotos?.[0];

    return (
        <ProductBaseTile
            viewer={viewer}
            itemSearch={itemSearch}
            item={item}
            user={user}
            afterDisplay={afterDisplay}
            beforeRender={beforeRender}
            currency={currency}
            index={index}
            isHovering={isHovering}
            isSponsored={isSponsored}
            hideRecentSoldPrice={hideRecentSoldPrice}
            measurementUnit={measurementUnit}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            shippingPrices={shippingPrices}
            onContentVisible={onContentVisible}
            setIsHovering={val => {
                setIsHovering(val);
                if (wasHovered.current) {
                    wasHovered.current = true;
                }
            }}
            size={size}
            showMskuSwatch={showMskuSwatch}
            showBrowseSimilar={showBrowseSimilar}
            showMeasurements={showMeasurements}
            showPrice={showPrice}
            hasQuickView={hasQuickView}
            showQuickViewButton={hasQuickView && showQuickViewButton}
            favoritesComponent={favoritesComponent}
            urgencySignal={urgencySignal}
            showShippingCost={showShippingCost}
            isShippingCostSbVariant={isShippingCostSbVariant}
            tileElementRef={tileElementRef}
        >
            <ProductImage
                item={item}
                photo={photos}
                disableLinks={disableLinks}
                backgroundColor={backgroundColor}
                imageDraggable={false}
                imageLoadVerticalOffset={imageLoadVerticalOffset}
                isEagerImageLoadEnabled={index < itemsPerRow}
                hasVideo={false}
                onClick={onClick}
                onContentLoaded={onContentLoaded}
                size={size}
                srcSet={srcSet}
            />
        </ProductBaseTile>
    );
};
